import { call, takeLatest, select, put } from 'redux-saga/effects';
import { AxiosResponse } from 'axios';
import { makeBookingManagerApi, IBookingManagerBreakdownViewResponse } from 'services/BookingManagerApi';
import * as BreakdownActions from '../actions';
import { getGuestInformationSuccessAction, getGuestInformationRequestAction } from '../../guestInformation/actions';
import { getOwnershipSuccessAction, getOwnershipRequestAction } from '../../ownership/actions';
import * as ExternalIdsActions from '../../externalIds/actions';
import * as BespokeNegotiationActions from '../../bespokeNegotiation/actions';
import {
  getBookingStatusOptionsSuccessAction,
  getBookingStatusOptionsRequestAction,
} from '../../bookingStatusOptions/actions';
import {
  getFinanceDocumentSuccessAction,
  getFinanceDocumentRequestAction,
  getFinanceDocumentBalanceSuccessAction,
  getFinanceDocumentBalanceRequestAction,
} from '../../finance/actions';
import { getHotelDetailsSuccessAction, getHotelDetailsRequestAction } from '../../hotelDetails/actions';
import { networkRequestsSelector as hotelDetailsNetworkRequestsSelector } from '../../hotelDetails/selectors';
import { toFrontendBreakdownWithExchangeRate } from './saga';
import { ENetworkRequestStatus } from 'services/BackendApi/types/Generic';

import { bookingUuidSelector, bookingSelector } from '../../../selectors';

/**
 * Loads the `booking-manager-breakdown` BMS view - a single request that returns the same
 * data as the individual endpoints, each key in the exact shape of its individual endpoint -
 * and fans the response out into the existing success actions, so all the reducers/selectors
 * keep working unchanged.
 *
 * If the view request fails, we degrade gracefully by dispatching the individual request
 * actions instead (the pre-view behaviour), honouring the same role/feature conditions the
 * container would have applied (passed on the action as `fallbacks`).
 */
export function* getBookingManagerBreakdownViewSaga(
  action: BreakdownActions.GetBookingManagerBreakdownViewRequestAction
) {
  const bookingManagerApi = makeBookingManagerApi();
  const bookingUuid = yield select(bookingUuidSelector);

  try {
    const result: AxiosResponse<IBookingManagerBreakdownViewResponse> = yield call(
      bookingManagerApi.getBookingManagerBreakdown,
      bookingUuid
    );
    const breakdownView = result.data;

    // keys the user has no read permission for are omitted by the backend
    if (breakdownView.guestDetails) {
      yield put(getGuestInformationSuccessAction(breakdownView.guestDetails.guests));
    }
    if (breakdownView.membership) {
      yield put(BreakdownActions.getMembershipSuccessAction(breakdownView.membership.membershipData));
    }
    if (breakdownView.ownership) {
      yield put(getOwnershipSuccessAction(breakdownView.ownership.ownershipData));
    }
    if (breakdownView.externalIds) {
      yield put(ExternalIdsActions.getSuccessAction(breakdownView.externalIds.externalIds));
    }
    if (breakdownView.barCommission) {
      yield put(
        BreakdownActions.getBookingBarCommissionSuccessAction(
          breakdownView.barCommission.commission?.percentage ?? null,
          breakdownView.barCommission.commission?.amount ?? null
        )
      );
    }
    if (breakdownView.bespokeNegotiation) {
      yield put(BespokeNegotiationActions.getSuccessAction(breakdownView.bespokeNegotiation.bespokeNegotiation));
    }
    if (breakdownView.bookingStatusOptions) {
      yield put(getBookingStatusOptionsSuccessAction(breakdownView.bookingStatusOptions.availableOptions));
    }
    // the Breakdown page always renders the SALES finance data (used for the "real"
    // outstanding total), so the sales document fans out into the finance success actions
    if (breakdownView.financeSales) {
      yield put(getFinanceDocumentSuccessAction({ rows: breakdownView.financeSales.rows }));
    }
    if (breakdownView.financeSalesBalance) {
      yield put(
        getFinanceDocumentBalanceSuccessAction({
          totalCreditCents: breakdownView.financeSalesBalance.totalCreditCents,
          totalDebitCents: breakdownView.financeSalesBalance.totalDebitCents,
          totalOutstandingCents: breakdownView.financeSalesBalance.totalOutstandingCents,
        })
      );
    }
    // latest-version copies of the policy documents - version switching afterwards still
    // re-fetches them version-pinned via SET_HEADLINE_BREAKDOWN_SELECTED_VERSION
    if (breakdownView.paymentTerms) {
      yield put(BreakdownActions.getPaymentTermsSuccessAction(breakdownView.paymentTerms.paymentTerms));
    }
    if (breakdownView.cancellationPolicies) {
      yield put(
        BreakdownActions.getCancellationPoliciesSuccessAction(breakdownView.cancellationPolicies.cancellationPolicies)
      );
    }
    if (breakdownView.offerTerms) {
      yield put(BreakdownActions.getOfferTermsSuccessAction(breakdownView.offerTerms.offerTerms));
    }
    if (breakdownView.policiesAndRestrictions) {
      yield put(
        BreakdownActions.getPoliciesAndRestrictionsSuccessAction(
          breakdownView.policiesAndRestrictions.policiesAndRestrictions
        )
      );
    }
    // hotel details are not editable in the app - only dispatch the success when they are
    // not already in the store, because the success action also triggers the hotel
    // suppliers fetch and we don't want to repeat that on every tab visit
    if (breakdownView.hotelDetails) {
      const { hotelDetailsLoad } = yield select(hotelDetailsNetworkRequestsSelector);
      if (hotelDetailsLoad !== ENetworkRequestStatus.SUCCESS) {
        yield put(getHotelDetailsSuccessAction(breakdownView.hotelDetails.hotelDetails));
      }
    }
    // the version list and headline document are bundled in the view so the page doesn't
    // have to make the sequential versions -> document round trips. the selected version is
    // set with the SILENT action so the version-selection sagas don't re-fetch the documents
    // we already have
    if (breakdownView.headlineBreakdownVersionList) {
      const latestVersion = breakdownView.headlineBreakdownVersionList[0];
      yield put(
        BreakdownActions.getHeadlineBreakdownVersionListSuccessAction(breakdownView.headlineBreakdownVersionList)
      );
      yield put(BreakdownActions.setHeadlineBreakdownSelectedVersionSilentAction(latestVersion));
      yield put(BreakdownActions.setHeadlineBreakdownRealLatestVersionAction(latestVersion));
    }
    if (breakdownView.headlineLineItemBreakdown) {
      const bookingItem = yield select(bookingSelector);
      yield put(
        BreakdownActions.getHeadlineLineItemBreakdownSuccessAction(
          toFrontendBreakdownWithExchangeRate(breakdownView.headlineLineItemBreakdown, bookingItem?.createdAt),
          breakdownView.headlineLineItemBreakdown.updatedAt
        )
      );
      yield put(BreakdownActions.clearAllSupplierValidationErrorsAction());
    }

    yield put(BreakdownActions.getBookingManagerBreakdownViewSuccessAction());
  } catch (e) {
    yield put(BreakdownActions.getBookingManagerBreakdownViewFailureAction(e));

    // graceful degradation - fall back to the individual requests the view replaces
    yield put(getGuestInformationRequestAction());
    yield put(BreakdownActions.getMembershipRequestAction());
    yield put(getOwnershipRequestAction());
    yield put(ExternalIdsActions.getRequestAction());
    yield put(getFinanceDocumentRequestAction());
    yield put(getFinanceDocumentBalanceRequestAction());
    // restores the versions -> document chain the view replaces (this also re-fetches the
    // payment/cancellation/offer/policies documents via SET_HEADLINE_BREAKDOWN_SELECTED_VERSION)
    yield put(BreakdownActions.getHeadlineBreakdownVersionListRequestAction());
    yield put(getHotelDetailsRequestAction());
    if (action.fallbacks.withBookingStatusOptions) {
      yield put(getBookingStatusOptionsRequestAction());
    }
    if (action.fallbacks.withBarCommission) {
      yield put(BreakdownActions.getBookingBarCommissionRequestAction());
    }
    if (action.fallbacks.withBespokeNegotiation) {
      yield put(BespokeNegotiationActions.getRequestAction());
    }
  }
}

export function* watchGetBookingManagerBreakdownViewSaga() {
  yield takeLatest(
    BreakdownActions.GET_BOOKING_MANAGER_BREAKDOWN_VIEW_REQUEST,
    getBookingManagerBreakdownViewSaga
  );
}
